import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Eyebrow } from "../CTA";
import { PROCESS } from "../../lib/site";

const Step = ({ item, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const left = index % 2 === 0;
  return (
    <div ref={ref} className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-4 py-6 md:gap-8">
      {/* left slot */}
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`${left ? "md:text-right" : "md:opacity-0 md:pointer-events-none"} ${left ? "" : "hidden md:block"}`}
      >
        {left && <Card item={item} align="right" />}
      </motion.div>

      {/* center node */}
      <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border border-white/15 bg-ink">
        <motion.div
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-brand font-display text-sm font-bold text-white"
        >
          {item.step}
        </motion.div>
      </div>

      {/* right slot */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`${!left ? "" : "hidden md:block md:opacity-0"}`}
      >
        {!left ? <Card item={item} align="left" /> : null}
      </motion.div>

      {/* mobile card always right */}
      {left && (
        <div className="col-start-3 md:hidden">
          <Card item={item} align="left" />
        </div>
      )}
    </div>
  );
};

const Card = ({ item, align }) => (
  <div className={`inline-block max-w-sm rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-md ${align === "right" ? "md:ml-auto" : ""}`}>
    <h3 className="font-display text-lg font-semibold text-white">{item.title}</h3>
    <p className="mt-1 text-sm text-white/55">{item.desc}</p>
  </div>
);

const ProcessTimeline = ({ withHeader = true }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end center"] });
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section className="relative overflow-hidden bg-ink py-24 grain sm:py-32" data-testid="process-section">
      <div className="blueprint-grid absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-[1200px] px-5 sm:px-8">
        {withHeader && (
          <div className="mx-auto max-w-3xl text-center">
            <div className="flex justify-center"><Eyebrow dark>Manufacturing Process</Eyebrow></div>
            <h2 className="mt-5 font-display text-4xl font-medium tracking-tight text-white sm:text-5xl">
              From raw material to dispatch.
            </h2>
            <p className="mt-4 text-white/55">A fully integrated 12-stage precision workflow under one roof.</p>
          </div>
        )}

        <div ref={ref} className="relative mt-16">
          {/* base line */}
          <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white/10" />
          {/* progress line */}
          <motion.div
            style={{ height }}
            className="absolute left-1/2 top-0 w-px -translate-x-1/2 bg-brand-soft shadow-[0_0_18px_rgba(59,130,246,0.9)]"
          />
          {PROCESS.map((item, i) => (
            <Step key={item.step} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
